/**
 * Custom element for managing location selection (store and delivery postcode) via a drawer interface.
 * Extends DrawerComponent.
 */
class LocationDrawer extends DrawerComponent {

  constructor() {
    super();

    this.selectors = {
      locationModalTrigger: '[data-location-modal-trigger]',
      postcodeForm: '.postcode-form',
    };

    this.classes = {
      editForm: 'postcode-form--edit',
    };
      
    this._handleOpenLocationDrawer = this._handleOpenLocationDrawer.bind(this);
    this._handleContextUpdate = this._handleContextUpdate.bind(this);

    // 99B 20251215 -PO, open location draw event handled differently across site. For example:
    // Open directly to edit C&C or delivery location, or refresh based on product or cart availability.
    // Here, keep track of event listener functions added, to remove on disconnectedCallback
    this.handleOpenLocationDrawerFunctions = new Map();
  }

  /**
   * Lifecycle callback executed when the element is added to the DOM.
   * Adds event listeners.
   */
  connectedCallback() {
    super.connectedCallback();

    document.querySelectorAll(this.selectors.locationModalTrigger).forEach(trigger => {
      const handleOpenLocationDrawerFunction = () => { return this._handleOpenLocationDrawer(trigger) };
      trigger?.addEventListener('click', handleOpenLocationDrawerFunction);
      this.handleOpenLocationDrawerFunctions.set(trigger, handleOpenLocationDrawerFunction);
    });

    this.contextUpdateUnsubscribe = FoxTheme.pubsub.subscribe(
      FoxTheme.pubsub.PUB_SUB_EVENTS.locationDrawerContextUpdated,
      this._handleContextUpdate
    );
  }

  /**
   * Lifecycle callback executed when the element is removed from the DOM.
   * Removes event listeners.
   */
  disconnectedCallback() {
    super.disconnectedCallback();

    document.querySelectorAll(this.selectors.locationModalTrigger).forEach(trigger => {
      const handleOpenLocationDrawerFunction = this.handleOpenLocationDrawerFunctions.get(trigger);
      if(handleOpenLocationDrawerFunction) trigger?.removeEventListener('click', handleOpenLocationDrawerFunction);
    });

    this.contextUpdateUnsubscribe?.();
  }

  /**
   * 99B 20251215 -PO, moved to own function to remove at end of lifetime (had memory leak)
   * Handles click to open location drawer
   * @param {object} trigger - The trigger button the call is made from
   * @private
   */
  _handleOpenLocationDrawer(trigger) {
    const context = trigger.dataset.modalContext;
    // Refresh the store cards & statuses depending on the context the modal was opened from
    if (context === 'cart' || context === 'product' || (!context && this.context)) {
      this.context = context;
      FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.locationDrawerContextUpdated, {
          context
      });
    }

    this.show(); 

    // TODO: Refactor to nicer solution...
    // Toggle with change delivery address form open
    if (trigger.dataset.locationModalTrigger === 'changeDeliverTo') {
      this.querySelectorAll(this.selectors.postcodeForm)[1].classList.add(
        this.classes.editForm
      );
    }

    if (trigger.dataset.locationModalTrigger === 'selectPickUpStore') {
      this.querySelectorAll(this.selectors.postcodeForm)[0].classList.add(
        this.classes.editForm
      );
    }
  }

  _handleContextUpdate(data) {
    this.context = data.context;
  }

  /**
   * Shows the drawer and traps focus.
   * @param {HTMLElement | null} [focusElement=null] - Element to focus upon opening.
   * @param {boolean} [animate=true] - Whether to animate the opening.
   */
  show(focusElement = null, animate = true) {
    super.show(focusElement, animate);

    if (this.open && !Shopify.designMode) {
      FoxTheme.a11y.trapFocus(this, this.focusElement);
    }
  }
}
customElements.define('location-drawer', LocationDrawer);
